import styled from 'styled-components';

// Shared with FlowLayout (../../styles.ts): the PDF viewer, the panel and the
// grids inside it all stack at the same width.
export const STACK_AT = '1200px';

export const PdfViewerWrapper = styled.div`
  flex: 3.5;
  min-width: 0;

  @media (max-width: ${STACK_AT}) {
    flex: 1 1 100%;
  }
`;

export const PanelWrapper = styled.div`
  flex: 3;
  min-width: 0;
  max-height: 85vh;
  overflow-y: auto;
  padding: 0 1rem;

  @media (max-width: ${STACK_AT}) {
    flex: 1 1 100%;
  }

  /* The DatePicker Container carries z-index: 9999 and sits in a grid, so it
     paints over the reject dropdown's open list from the statement above.
     Neutralize it here; the calendar popper keeps its own layer below. */
  .asset-verification-date-picker {
    width: 100%;
    z-index: auto;
    /* The picker ships a 1.7rem bottom margin meant for standalone forms; here
       the grid gap already spaces the stacked start and end dates. */
    margin-bottom: 0;

    .react-datepicker__tab-loop {
      z-index: 20;
    }
  }

  /* Inline field errors (InputText's HelperText) sit flush against the input. */
  .HelperText {
    margin-top: 0.35rem;
  }
`;

export const SectionHeading = styled.h3`
  ${({ theme }) => theme.font.normal}
  font-weight: 700;
  margin: 0 0 0.75rem;
`;

export const FieldLabel = styled.div`
  margin-bottom: 0.35rem;
`;

// Grid over Row/Row.Col flex: the Col sizes never added to 12, so between the
// mobile query and full width the pickers overflowed their columns. min-width: 0
// on every cell lets react-datepicker's input shrink instead of overflowing.
export const FieldPair = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
  align-items: start;

  > * {
    min-width: 0;
  }

  @media (max-width: ${STACK_AT}) {
    grid-template-columns: minmax(0, 1fr);
    gap: 0.5rem;
  }
`;

// Below this width the 2fr column can't hold two side-by-side date pickers:
// the year clips under the calendar icon (verified in-browser at 1250-1550).
const DATE_PAIR_STACK_AT = '1650px';

// Each cell is a label row plus an input row on a subgrid, so a label that
// wraps ("Starting balance" at narrow widths) grows the label row for every
// cell and the inputs stay on one line with the date fields.
export const StatementGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1fr);
  grid-template-rows: auto auto;
  gap: 1rem;
  align-items: start;

  > * {
    min-width: 0;
    display: grid;
    grid-row: span 2;
    grid-template-rows: subgrid;
    row-gap: 0;
  }

  /* Columns are sized for their inputs, which are wider than any label, so
     the labels never need a second line. */
  ${FieldLabel} {
    white-space: nowrap;
  }

  /* Once the dates stack, the period column no longer needs double width, and
     equal thirds give each balance room for an eight-digit amount. */
  @media (max-width: ${DATE_PAIR_STACK_AT}) {
    grid-template-columns: repeat(3, minmax(0, 1fr));

    ${FieldPair} {
      grid-template-columns: minmax(0, 1fr);
      gap: 0.5rem;
    }
  }

  @media (max-width: ${STACK_AT}) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const StatementDivider = styled.hr`
  border: none;
  border-top: 1px solid ${({ theme }) => theme.color.gray[200]};
  margin: 1.25rem 0;
`;

// Separates one statement's fields and controls from the next inside an
// account, so "Reject this statement" reads as belonging to the fields above it.
// The last block keeps its rule too: it is what parts "Remove statement" from
// the "Add another statement" button below.
export const StatementBlock = styled.div`
  padding-bottom: 1.5rem;
  margin-bottom: 1.5rem;
  border-bottom: 1px dashed ${({ theme }) => theme.color.gray[200]};
`;

// Lifts the open reason list above the neighbouring date fields.
export const RejectControl = styled.div`
  position: relative;
  z-index: 10;
`;

// Same visual treatment as the fraud review's OfferLetterReasonChip, plus an
// inline dismiss button (un-selecting in the dropdown does the same thing).
export const RejectReasonChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 500;
  color: #92400e;
  background: #fef3c7;
  padding: 2px 8px;
  border-radius: 12px;
  margin: 2px 4px 2px 0;
`;

export const ChipDismissButton = styled.button`
  border: none;
  background: none;
  color: inherit;
  font-size: 12px;
  line-height: 1;
  padding: 0;
  cursor: pointer;
`;

// DropdownButton renders a styled.div with role="button" (class DDButton), NOT a
// real <button> — target the classes. DDButton is the bordered box: pin it to the
// InputText footprint (same 0.125rem gray border already; radius 0.25rem to match).
export const DropdownFill = styled.div`
  [role='dropdown'] {
    width: 100%;

    .DDButton {
      width: 100%;
      /* InputText footprint: 2.125rem input + 2x0.313rem padding + 2x0.125rem border */
      height: 3rem;
      border-radius: 0.25rem;
      display: flex;
      align-items: center;
    }

    .DDSubContainer {
      width: 100%;
      height: 100%;
      padding-top: 0;
      padding-bottom: 0;
      display: flex;
      align-items: center;
    }
  }
`;
